/**
 * PortalProyectos — pestaña "Proyectos" del Portal de Clientes.
 *
 * Listado: tarjetas con avance, fechas, tareas y comentarios nuevos.
 * Detalle: resumen, novedades desde la última visita, tareas con subtareas y
 * conversación por tarea, y "Comentarios y consultas" del proyecto.
 *
 * Solo lectura sobre proyectos y tareas: los datos llegan por las RPC
 * portal_mis_proyectos / portal_proyecto_detalle, que la base de datos filtra
 * según la identidad autenticada. El cliente únicamente puede comentar.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../lib/supabase";
import {
  ChevronLeft, ChevronDown, ChevronRight, CheckCircle2, Circle, Loader,
  AlertCircle, Calendar, Clock, MessageSquare, Sparkles, RefreshCw,
} from "lucide-react";
import ComentariosHilo from "./comentarios/ComentariosHilo";
import {
  resumenComentarios, marcarLectura, suscribirComentarios, mensajeError, hiloDe, HILO_GENERAL,
} from "../lib/comentarios";
import { progresoSubtareas } from "../lib/progreso";
import { fmtFecha, fmtFechaHora, fmtRelativo, diasRestantes } from "../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
  purple:"#818cf8", purpleL:"#eef2ff",
};

const PROY_EST = {
  Activo:     { color:C.accent, bg:C.accentL },
  "En pausa": { color:C.warn,   bg:C.warnL   },
  Completado: { color:C.ok,     bg:C.okL     },
  Cancelado:  { color:C.err,    bg:C.errL    },
};

const ESTADOS = [
  { key:"Por hacer",   label:"Pendientes",  color:"#94a3b8", bg:"#f1f5f9" },
  { key:"En progreso", label:"En desarrollo", color:C.warn,  bg:C.warnL },
  { key:"En revisión", label:"En revisión", color:C.purple,  bg:C.purpleL },
  { key:"Completado",  label:"Completadas", color:C.ok,      bg:C.okL },
];
const estadoInfo = (k) => ESTADOS.find(e => e.key === k) || ESTADOS[0];

const card = {
  background:C.white, border:`1px solid ${C.border}`, borderRadius:12,
  boxShadow:"0 1px 3px #0000000a",
};

export default function PortalProyectos({ proyectos, cargando, error, onRefrescar, proyectoInicial }) {
  const [abierto, setAbierto] = useState(proyectoInicial || null);

  useEffect(() => { if (proyectoInicial) setAbierto(proyectoInicial); }, [proyectoInicial]);

  if (abierto) {
    return (
      <DetalleProyecto proyectoId={abierto}
        onVolver={() => { setAbierto(null); onRefrescar?.(); }}/>
    );
  }

  if (cargando) return <Estado icono={<Loader size={16} className="spinning"/>} texto="Cargando proyectos..."/>;
  if (error) return <Estado error texto={error} accion={onRefrescar}/>;
  if (!proyectos.length) return <Estado texto="Aún no tienes proyectos asociados. Si crees que es un error, escríbenos a contacto@tuaplicacion.cl."/>;

  return (
    <div className="portal-grid">
      {proyectos.map(p => <TarjetaProyecto key={p.id} p={p} onVer={() => setAbierto(p.id)}/>)}
    </div>
  );
}

// ── Tarjeta del listado ────────────────────────────────────────────────────
function TarjetaProyecto({ p, onVer }) {
  const est = PROY_EST[p.estado] || PROY_EST.Activo;
  const pct = Number(p.progreso_pct) || 0;
  const total = Number(p.total_tareas) || 0;
  const nuevos = Number(p.comentarios_nuevos) || 0;
  return (
    <article style={{...card, padding:"18px 20px", display:"flex", flexDirection:"column", gap:12}}
      aria-labelledby={`proy-${p.id}`}>
      <div style={{display:"flex", justifyContent:"space-between", gap:10, alignItems:"flex-start"}}>
        <div style={{minWidth:0}}>
          <h3 id={`proy-${p.id}`} style={{margin:0, fontSize:16, fontWeight:700, color:C.text}}>
            {p.nombre}
          </h3>
          <span style={{display:"inline-block", marginTop:6, padding:"2px 10px", borderRadius:12,
            fontSize:11, fontWeight:600, color:est.color, background:est.bg}}>{p.estado}</span>
        </div>
        <div style={{textAlign:"right", flexShrink:0}}>
          <div style={{fontWeight:800, fontSize:24, color: pct === 100 ? C.ok : C.accent}}>{pct}%</div>
          <div style={{fontSize:11, color:C.light}}>avance</div>
        </div>
      </div>

      <Barra pct={pct}/>

      <dl style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 12px", margin:0, fontSize:12}}>
        <Dato label="Inicio" valor={fmtFecha(p.fecha_inicio)}/>
        <Dato label="Entrega estimada" valor={fmtFecha(p.fecha_entrega)}/>
        <Dato label="Completadas" valor={total ? `${p.tareas_completadas} de ${total}` : "Sin tareas aún"}/>
        <Dato label="Pendientes" valor={total ? String(p.tareas_pendientes) : "—"}/>
      </dl>

      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
        paddingTop:10, borderTop:`1px solid ${C.border}`, flexWrap:"wrap"}}>
        <span style={{display:"flex", alignItems:"center", gap:6, fontSize:12,
          color: nuevos ? C.accent : C.sub, fontWeight: nuevos ? 700 : 500}}>
          <MessageSquare size={13} aria-hidden="true"/>
          {nuevos
            ? `${nuevos} comentario${nuevos > 1 ? "s" : ""} nuevo${nuevos > 1 ? "s" : ""}`
            : "Sin comentarios nuevos"}
        </span>
        <button type="button" onClick={onVer} style={btnPrimario}>Ver proyecto</button>
      </div>
    </article>
  );
}

// ── Detalle ────────────────────────────────────────────────────────────────
function DetalleProyecto({ proyectoId, onVolver }) {
  const [p,        setP]        = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState("");
  const [resumen,  setResumen]  = useState({});
  const [filtro,   setFiltro]   = useState("todas");
  const [ultimaVisita, setUltimaVisita] = useState(null);

  const cargarResumen = useCallback(async () => {
    try {
      const filas = await resumenComentarios(proyectoId);
      const porHilo = {};
      filas.forEach(r => { porHilo[hiloDe(r.tarea_id)] = r; });
      setResumen(porHilo);
    } catch { /* los contadores son secundarios; el hilo muestra su propio error */ }
  }, [proyectoId]);

  const cargar = useCallback(async () => {
    setCargando(true); setError("");
    const { data, error: e } = await supabase.rpc("portal_proyecto_detalle", { p_proyecto_id: proyectoId });
    if (e) { setError(mensajeError(e)); setCargando(false); return null; }
    if (!data) { setError("El proyecto no existe o ya no está disponible."); setCargando(false); return null; }
    setP(data);
    setCargando(false);
    return data;
  }, [proyectoId]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const data = await cargar();
      if (!data || !vivo) return;
      setUltimaVisita(data.ultima_visita);      // la visita ANTERIOR define las novedades
      cargarResumen();
      marcarLectura(proyectoId, "visita").catch(() => {});
    })();
    return () => { vivo = false; };
  }, [proyectoId, cargar, cargarResumen]);

  useEffect(() => suscribirComentarios(proyectoId, { onInsert: () => cargarResumen() }),
    [proyectoId, cargarResumen]);

  const tareas = useMemo(() => p?.tareas || [], [p]);
  const novedades = useMemo(() => {
    if (!ultimaVisita) return [];
    return tareas.filter(t => t.updated_at && new Date(t.updated_at) > new Date(ultimaVisita));
  }, [tareas, ultimaVisita]);
  const recientes = useMemo(() =>
    [...tareas].filter(t => t.updated_at)
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 5),
  [tareas]);
  const nuevosTotal = Object.values(resumen).reduce((s, r) => s + (r.nuevos || 0), 0);

  if (cargando && !p) return <Estado icono={<Loader size={16} className="spinning"/>} texto="Cargando proyecto..."/>;
  if (error) return (
    <div>
      <BotonVolver onClick={onVolver}/>
      <Estado error texto={error} accion={cargar}/>
    </div>
  );

  const est = PROY_EST[p.estado] || PROY_EST.Activo;
  const pct = Number(p.progreso_pct) || 0;
  const total = Number(p.total_tareas) || 0;
  const dias = diasRestantes(p.fecha_entrega);
  const conteo = Object.fromEntries(ESTADOS.map(e => [e.key, tareas.filter(t => t.estado === e.key).length]));
  const visibles = filtro === "todas" ? tareas : tareas.filter(t => t.estado === filtro);

  return (
    <div style={{display:"flex", flexDirection:"column", gap:16}}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:10}}>
        <BotonVolver onClick={onVolver}/>
        <button type="button" onClick={() => { cargar(); cargarResumen(); }} style={btnSecundario}
          aria-label="Actualizar proyecto">
          <RefreshCw size={13} aria-hidden="true"/> Actualizar
        </button>
      </div>

      {/* Resumen */}
      <section style={{...card, padding:"20px 22px"}} aria-labelledby="det-titulo">
        <div style={{display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap"}}>
          <div style={{minWidth:0, flex:1}}>
            <h2 id="det-titulo" style={{margin:0, fontSize:20, fontWeight:800, color:C.text}}>{p.nombre}</h2>
            <span style={{display:"inline-block", marginTop:8, padding:"3px 12px", borderRadius:12,
              fontSize:12, fontWeight:600, color:est.color, background:est.bg}}>{p.estado}</span>
            {p.descripcion && (
              <p style={{margin:"12px 0 0", fontSize:13, color:C.sub, lineHeight:1.6, whiteSpace:"pre-wrap"}}>
                {p.descripcion}
              </p>
            )}
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontWeight:800, fontSize:32, color: pct === 100 ? C.ok : C.accent}}>{pct}%</div>
            <div style={{fontSize:11, color:C.sub}}>avance del proyecto</div>
          </div>
        </div>

        <div style={{margin:"16px 0"}}>
          <Barra pct={pct} alto={8}/>
          <div style={{fontSize:12, color:C.sub, marginTop:6}}>
            {total
              ? `${p.tareas_completadas} de ${total} tareas completadas`
              : "El equipo aún no ha cargado las tareas de este proyecto."}
          </div>
        </div>

        <dl className="portal-meta" style={{margin:0, paddingTop:14, borderTop:`1px solid ${C.border}`}}>
          <Dato label="Inicio" valor={fmtFecha(p.fecha_inicio)} icono={<Calendar size={12}/>}/>
          <Dato label="Entrega estimada" valor={fmtFecha(p.fecha_entrega)}
            icono={<Clock size={12} color={dias !== null && dias < 7 && pct < 100 ? C.err : undefined}/>}/>
          {ESTADOS.map(e => (
            <Dato key={e.key} label={e.label} valor={String(conteo[e.key])}
              icono={<span style={{width:7, height:7, borderRadius:"50%", background:e.color, display:"inline-block"}}/>}/>
          ))}
        </dl>
      </section>

      {/* Novedades y últimas actualizaciones */}
      <section style={{...card, padding:"16px 20px"}} aria-labelledby="det-novedades">
        <h3 id="det-novedades" style={tituloSeccion}>
          <Sparkles size={15} color={C.accent} aria-hidden="true"/>
          {ultimaVisita ? "Desde tu última visita" : "Últimas actualizaciones"}
        </h3>
        {ultimaVisita && (
          <p style={{margin:"0 0 10px", fontSize:12, color:C.sub}}>
            Última visita: {fmtFechaHora(ultimaVisita)} ·{" "}
            {novedades.length
              ? `${novedades.length} tarea${novedades.length > 1 ? "s" : ""} actualizada${novedades.length > 1 ? "s" : ""}`
              : "sin cambios en tareas"}
            {" · "}
            {nuevosTotal
              ? <strong style={{color:C.accent}}>{nuevosTotal} comentario{nuevosTotal > 1 ? "s" : ""} nuevo{nuevosTotal > 1 ? "s" : ""}</strong>
              : "sin comentarios nuevos"}
          </p>
        )}
        {recientes.length === 0
          ? <p style={{margin:0, fontSize:13, color:C.sub}}>Aún no hay actividad registrada.</p>
          : (
            <ul style={{listStyle:"none", margin:0, padding:0, display:"flex", flexDirection:"column", gap:6}}>
              {recientes.map(t => {
                const e = estadoInfo(t.estado);
                const nueva = novedades.some(n => n.id === t.id);
                return (
                  <li key={t.id} style={{display:"flex", alignItems:"center", gap:8, fontSize:13}}>
                    <span style={{padding:"1px 8px", borderRadius:10, fontSize:10, fontWeight:700,
                      color:e.color, background:e.bg, flexShrink:0}}>{e.label}</span>
                    <span style={{flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis",
                      whiteSpace:"nowrap", color:C.text, fontWeight: nueva ? 700 : 400}}>{t.descripcion}</span>
                    {nueva && <span style={{fontSize:10, fontWeight:700, color:C.accent}}>NUEVO</span>}
                    <span style={{fontSize:11, color:C.light, flexShrink:0}}>{fmtRelativo(t.updated_at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
      </section>

      {/* Tareas */}
      <section aria-labelledby="det-tareas" style={{display:"flex", flexDirection:"column", gap:10}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap"}}>
          <h3 id="det-tareas" style={{...tituloSeccion, margin:0}}>Tareas del proyecto</h3>
          <div role="group" aria-label="Filtrar tareas por estado" style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            {[{ key:"todas", label:`Todas (${tareas.length})` },
              ...ESTADOS.map(e => ({ key:e.key, label:`${e.label} (${conteo[e.key]})` }))].map(f => (
              <button key={f.key} type="button" onClick={() => setFiltro(f.key)}
                aria-pressed={filtro === f.key}
                style={{padding:"5px 11px", borderRadius:16, fontSize:12, cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif", fontWeight:600,
                  border:`1px solid ${filtro === f.key ? C.accent : C.border}`,
                  background: filtro === f.key ? C.accent : C.white,
                  color: filtro === f.key ? "#fff" : C.sub}}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {visibles.length === 0 && (
          <div style={{...card, padding:"24px", textAlign:"center", fontSize:13, color:C.sub}}>
            {tareas.length ? "No hay tareas en este estado." : "El equipo aún no ha cargado las tareas del proyecto."}
          </div>
        )}
        {visibles.map(t => (
          <TareaPortal key={t.id} t={t} proyectoId={p.id}
            resumen={resumen[t.id]} nueva={novedades.some(n => n.id === t.id)}
            onActividad={cargarResumen}/>
        ))}
      </section>

      {/* Comentarios generales */}
      <section style={{...card, padding:"18px 20px"}}>
        <ComentariosHilo proyectoId={p.id} tareaId={null} modo="cliente"
          titulo={`Comentarios y consultas${resumen[HILO_GENERAL]?.nuevos ? ` · ${resumen[HILO_GENERAL].nuevos} nuevo${resumen[HILO_GENERAL].nuevos > 1 ? "s" : ""}` : ""}`}
          onActividad={cargarResumen}/>
      </section>
    </div>
  );
}

function TareaPortal({ t, proyectoId, resumen, nueva, onActividad }) {
  const [abierta, setAbierta] = useState(false);
  const e = estadoInfo(t.estado);
  const subs = t.subtareas || [];
  const sp = progresoSubtareas(subs);
  const idPanel = `tarea-panel-${t.id}`;
  const nuevos = resumen?.nuevos || 0;
  const total = resumen?.total || 0;

  return (
    <article style={{...card, overflow:"hidden", borderColor: nueva ? `${C.accent}60` : C.border}}>
      <button type="button" onClick={() => setAbierta(!abierta)}
        aria-expanded={abierta} aria-controls={idPanel}
        style={{width:"100%", display:"flex", alignItems:"flex-start", gap:10, padding:"13px 16px",
          background:"transparent", border:"none", cursor:"pointer", textAlign:"left",
          fontFamily:"'DM Sans',sans-serif"}}>
        {t.estado === "Completado"
          ? <CheckCircle2 size={17} color={C.ok} style={{flexShrink:0, marginTop:1}} aria-hidden="true"/>
          : <Circle size={17} color={e.color} style={{flexShrink:0, marginTop:1}} aria-hidden="true"/>}
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontWeight:600, fontSize:14, color:C.text, lineHeight:1.4}}>{t.descripcion}</div>
          <div style={{display:"flex", alignItems:"center", gap:8, marginTop:6, flexWrap:"wrap", fontSize:11}}>
            <span style={{padding:"1px 8px", borderRadius:10, fontWeight:700, color:e.color, background:e.bg}}>
              {e.label}
            </span>
            {t.fecha_fin && (
              <span style={{color:C.sub, display:"flex", alignItems:"center", gap:3}}>
                <Calendar size={10} aria-hidden="true"/> Prevista: {fmtFecha(t.fecha_fin)}
              </span>
            )}
            {sp.total > 0 && <span style={{color:C.sub}}>Subtareas {sp.completadas}/{sp.total} · {sp.pct}%</span>}
            {total > 0 && (
              <span style={{display:"flex", alignItems:"center", gap:3,
                color: nuevos ? C.accent : C.sub, fontWeight: nuevos ? 700 : 500}}>
                <MessageSquare size={10} aria-hidden="true"/>
                {total}{nuevos ? ` · ${nuevos} nuevo${nuevos > 1 ? "s" : ""}` : ""}
              </span>
            )}
            {nueva && <span style={{color:C.accent, fontWeight:700}}>Actualizada</span>}
          </div>
        </div>
        <span style={{fontSize:12, color:C.accent, fontWeight:600, display:"flex", alignItems:"center", gap:3, flexShrink:0}}>
          {abierta ? "Ocultar" : "Ver detalle"}
          {abierta ? <ChevronDown size={14} aria-hidden="true"/> : <ChevronRight size={14} aria-hidden="true"/>}
        </span>
      </button>

      {sp.total > 0 && (
        <div style={{padding:"0 16px 10px"}}><Barra pct={sp.pct} alto={3} color={C.ok}/></div>
      )}

      {abierta && (
        <div id={idPanel} style={{borderTop:`1px solid ${C.border}`, padding:"14px 16px",
          display:"flex", flexDirection:"column", gap:14, background:C.bg}}>
          <div style={{display:"flex", gap:16, flexWrap:"wrap", fontSize:12, color:C.sub}}>
            {t.fecha_inicio && <span>Inicio: <strong style={{color:C.text}}>{fmtFecha(t.fecha_inicio)}</strong></span>}
            <span>Fecha prevista: <strong style={{color:C.text}}>{t.fecha_fin ? fmtFecha(t.fecha_fin) : "Por definir"}</strong></span>
            {t.updated_at && <span>Última actualización: <strong style={{color:C.text}}>{fmtFechaHora(t.updated_at)}</strong></span>}
          </div>

          {sp.total > 0 && (
            <div>
              <div style={{fontSize:11, fontWeight:700, color:C.sub, textTransform:"uppercase",
                letterSpacing:.5, marginBottom:6}}>
                Subtareas · {sp.pct}% completado
              </div>
              <ul style={{listStyle:"none", margin:0, padding:0, display:"flex", flexDirection:"column", gap:5}}>
                {subs.map(s => (
                  <li key={s.id} style={{display:"flex", alignItems:"flex-start", gap:7, fontSize:13}}>
                    {s.completada
                      ? <CheckCircle2 size={14} color={C.ok} style={{flexShrink:0, marginTop:2}} aria-label="Completada"/>
                      : <Circle size={14} color={C.light} style={{flexShrink:0, marginTop:2}} aria-label="Pendiente"/>}
                    <span style={{color: s.completada ? C.light : C.text,
                      textDecoration: s.completada ? "line-through" : "none"}}>{s.descripcion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px"}}>
            <ComentariosHilo proyectoId={proyectoId} tareaId={t.id} modo="cliente"
              titulo="Comentarios de esta tarea" alturaMax={320}
              placeholder="Escribe una consulta sobre esta tarea…"
              onActividad={onActividad}/>
          </div>
        </div>
      )}
    </article>
  );
}

// ── Piezas pequeñas ────────────────────────────────────────────────────────
function Barra({ pct, alto = 6, color }) {
  return (
    <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
      aria-label={`Avance ${pct}%`}
      style={{height:alto, background:"#eef2f6", borderRadius:alto, overflow:"hidden"}}>
      <div style={{height:"100%", width:`${pct}%`, borderRadius:alto,
        background: color || (pct === 100 ? C.ok : C.accent), transition:"width .4s ease"}}/>
    </div>
  );
}

function Dato({ label, valor, icono }) {
  return (
    <div>
      <dt style={{fontSize:10, fontWeight:600, color:C.light, textTransform:"uppercase", letterSpacing:.5,
        display:"flex", alignItems:"center", gap:4}}>{icono}{label}</dt>
      <dd style={{margin:"3px 0 0", fontSize:13, fontWeight:600, color:C.text}}>{valor}</dd>
    </div>
  );
}

function BotonVolver({ onClick }) {
  return (
    <button type="button" onClick={onClick} style={btnSecundario}>
      <ChevronLeft size={14} aria-hidden="true"/> Mis proyectos
    </button>
  );
}

function Estado({ texto, icono, error, accion }) {
  return (
    <div role={error ? "alert" : undefined}
      style={{...card, padding:"32px 24px", textAlign:"center", fontSize:13,
        color: error ? C.err : C.sub, display:"flex", flexDirection:"column",
        alignItems:"center", gap:10}}>
      <div style={{display:"flex", alignItems:"center", gap:8}}>
        {error ? <AlertCircle size={16} aria-hidden="true"/> : icono}{texto}
      </div>
      {accion && <button type="button" onClick={accion} style={btnSecundario}>Reintentar</button>}
    </div>
  );
}

const tituloSeccion = {
  display:"flex", alignItems:"center", gap:7, fontSize:14, fontWeight:700,
  color:C.text, margin:"0 0 10px",
};
const btnPrimario = {
  padding:"8px 16px", borderRadius:8, border:"none", background:C.accent, color:"#fff",
  fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
};
const btnSecundario = {
  display:"inline-flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8,
  border:`1px solid ${C.border}`, background:C.white, color:C.sub, fontSize:12,
  fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
};
